import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import copy from "copy-to-clipboard";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { FC, ReactNode, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import * as s from "./styles";
import { BucketPolicyDialogProps } from "./types";

/*
  Matches one JSON token at a time: a string literal (followed by a colon when
  it is an object key), a boolean, null or a number. String literals come first
  in the alternation, so keywords and numbers inside a string are never matched
  on their own.
*/
const JSON_TOKEN_REGEX =
  /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false)\b|\b(null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

const highlightJson = (json: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  const regex = new RegExp(JSON_TOKEN_REGEX);
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(json)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(json.slice(lastIndex, match.index));
    }

    const [, stringToken, colon, booleanToken, nullToken, numberToken] = match;

    if (stringToken && colon) {
      nodes.push(<s.JsonKey key={match.index}>{stringToken}</s.JsonKey>, colon);
    } else if (stringToken) {
      nodes.push(<s.JsonString key={match.index}>{stringToken}</s.JsonString>);
    } else if (booleanToken) {
      nodes.push(
        <s.JsonBoolean key={match.index}>{booleanToken}</s.JsonBoolean>
      );
    } else if (nullToken) {
      nodes.push(<s.JsonNull key={match.index}>{nullToken}</s.JsonNull>);
    } else {
      nodes.push(<s.JsonNumber key={match.index}>{numberToken}</s.JsonNumber>);
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < json.length) {
    nodes.push(json.slice(lastIndex));
  }

  return nodes;
};

export const BucketPolicyDialog: FC<BucketPolicyDialogProps> = ({
  isOpened,
  policy,
  onClose
}: BucketPolicyDialogProps) => {
  const dispatch = useDispatch();

  const highlightedJson = useMemo(
    () => (policy ? highlightJson(policy.json) : null),
    [policy]
  );

  const handleCopyToClipboardButtonClick = useCallback(() => {
    if (!policy) {
      return;
    }
    copy(policy.json);
    dispatch(
      notificationsActions.showNotification({
        type: NOTIFICATION_TYPES.INFO,
        title: `${policy.label} has been copied to clipboard.`
      })
    );
  }, [dispatch, policy]);

  return (
    <s.Dialog
      data-testid={"bucket-policy-dialog"}
      open={isOpened}
      onClose={onClose}
    >
      <DialogTitle data-testid={"bucket-policy-dialog-title"}>
        {policy?.label}
      </DialogTitle>
      <s.Content>
        <s.Json data-testid={"bucket-policy-dialog-json"}>
          {highlightedJson}
        </s.Json>
      </s.Content>
      <DialogActions>
        <Button
          color={"secondary"}
          onClick={handleCopyToClipboardButtonClick}
          disabled={!policy}
        >
          Copy to clipboard
        </Button>
        <Button
          color={"secondary"}
          onClick={onClose}
          data-testid={"bucket-policy-dialog-close-button"}
        >
          Close
        </Button>
      </DialogActions>
    </s.Dialog>
  );
};
